import type { components } from '@/generated/contracts/api-v1';

type Timer = ReturnType<typeof setTimeout> | number;
type Sighting = components['schemas']['ReportClientSightingRequest'];
type Receipt = components['schemas']['ClientSightingReceiptDto'];
type Options = {
  /** Sends the sighting (reportClientSighting); null when it was not answered with a receipt. */
  report: (sighting: Sighting, signal: AbortSignal) => Promise<Receipt | null>;
  /** This deployment's environment (getVersion), or null when unread. */
  environment: (signal: AbortSignal) => Promise<string | null>;
  schedule?: (callback: () => void, delay: number) => Timer;
  cancel?: (timer: Timer) => void;
};

// Receipt retries are independent of update advice and end only after durable
// acknowledgment of this exact identity. Outside production nothing is recorded
// durably, so a staging deployment ends them once it is known to be staging.
export function startReleaseSighting(sighting: Sighting, options: Options) {
  const { report: send, environment, schedule = setTimeout, cancel = clearTimeout } = options;
  const expected = { platform: sighting.platform || '', version: sighting.version || '',
    sha: (sighting.sha || '').toLowerCase(), unitySha: (sighting.unitySha || '').toLowerCase() };
  let stopped = false, delay = 2000;
  let retry: Timer | undefined;
  let timeout: Timer | undefined;
  let controller: AbortController | undefined;
  async function report() {
    if (stopped) return;
    controller = new AbortController();
    timeout = schedule(() => controller?.abort(), 10000);
    try {
      const receipt = await send(sighting, controller.signal), identity = receipt?.identity;
      if (receipt?.recorded === true && /^[a-f0-9]{40}$/.test(expected.sha) && identity &&
          identity.platform === expected.platform && identity.version === expected.version &&
          identity.sha === expected.sha && (identity.unitySha || '') === expected.unitySha) {
        stopped = true;
      } else if (receipt && (await environment(controller.signal))?.toLowerCase() === 'staging') {
        stopped = true;
      }
    } catch { /* Transient failures retry; never manufacture a receipt. */ }
    finally { if (timeout !== undefined) cancel(timeout); timeout = undefined; }
    if (!stopped) { retry = schedule(() => void report(), delay); delay = Math.min(delay * 2, 60000); }
  }
  void report();
  return () => {
    stopped = true;
    if (retry !== undefined) cancel(retry);
    if (timeout !== undefined) cancel(timeout);
    controller?.abort();
  };
}
